import {
  doc,
  setDoc,
  updateDoc,
  collection,
  query,
  onSnapshot,
  serverTimestamp,
  deleteField,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import { calcDistance } from '../lib/distance';
import type { NearbyUser } from '../types/location';

/**
 * The Expo mobile app stores user location directly in the 'users' collection
 * (fields: lat, lng, updatedAt). We also write to 'users' for compatibility,
 * and read from 'users' to see all users including mobile app users.
 */
const USERS_COLLECTION = 'users';

/**
 * Update my real-time location (writes to 'users' collection for Expo compatibility).
 *
 * Apple Guideline 5.1.2 compliance:
 * When `shareOnMap` is false, lat/lng fields are REMOVED from the user doc so the user
 * does not appear on other users' maps / nearby lists. The user can still send messages
 * and chat on their frequency, but their persistent map marker is hidden.
 */
export async function updateMyLocation(
  uid: string,
  lat: number,
  lng: number,
  nickname: string,
  gender: 'male' | 'female' | 'unknown',
  profileImageUrl?: string,
  shareOnMap: boolean = false,
): Promise<void> {
  const base: Record<string, unknown> = {
    nickname,
    gender,
    profileImageUrl: profileImageUrl ?? null,
    isOnline: true,
    appVersion: __APP_VERSION__,
    lastActiveAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (shareOnMap) {
    base.lat = lat;
    base.lng = lng;
  } else {
    // Explicitly clear lat/lng so prior session's values do not keep exposing the user on the map.
    base.lat = deleteField();
    base.lng = deleteField();
  }
  await setDoc(doc(db, USERS_COLLECTION, uid), base, { merge: true });
}

/**
 * Clear my lat/lng from the user document (remove myself from the map).
 * Called when the user toggles map sharing off mid-session.
 */
export async function clearMyLocationOnMap(uid: string): Promise<void> {
  try {
    await updateDoc(doc(db, USERS_COLLECTION, uid), {
      lat: deleteField(),
      lng: deleteField(),
      updatedAt: serverTimestamp(),
    });
  } catch {
    // Document might not have lat/lng set yet — ignore
  }
}

/**
 * Go offline - mark as offline instead of deleting.
 * Preserves last known location.
 */
export async function goOffline(uid: string): Promise<void> {
  try {
    await updateDoc(doc(db, USERS_COLLECTION, uid), {
      isOnline: false,
      manualFreqApplied: false,
      updatedAt: serverTimestamp(),
    });
  } catch {
    // Document might not exist yet
  }
}

/**
 * Subscribe to all users from 'users' collection.
 * Filters by activeThresholdMinutes (default 60 min).
 * Only includes users that have lat/lng fields set.
 * Sorted by distance (closest first).
 */
export function subscribeAllUsers(
  myUid: string,
  myLat: number,
  myLng: number,
  onUpdate: (users: NearbyUser[]) => void,
  activeThresholdMinutes: number = 60,
): Unsubscribe {
  const q = query(collection(db, USERS_COLLECTION));

  return onSnapshot(q, (snap) => {
    const now = Date.now();
    const thresholdMs = activeThresholdMinutes * 60 * 1000;
    const users: NearbyUser[] = [];

    for (const docSnap of snap.docs) {
      const data = docSnap.data();
      const uid = docSnap.id;
      if (uid === myUid) continue;
      if (typeof data.lat !== 'number' || typeof data.lng !== 'number') continue;

      if (data.updatedAt) {
        const updatedMs = data.updatedAt.toMillis ? data.updatedAt.toMillis() : new Date(data.updatedAt).getTime();
        if (now - updatedMs > thresholdMs) continue;
      }

      users.push(buildNearbyUser(uid, data, myLat, myLng));
    }

    users.sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));
    onUpdate(users);
  }, (err) => {
    console.warn('[Location] All users listener error:', err.code ?? err.message);
  });
}

/**
 * Subscribe to recent users for the "nearby users" list page.
 * No time threshold - shows all users with lat/lng, sorted by last activity.
 * Limited to maxCount users.
 */
export function subscribeRecentUsers(
  myUid: string,
  myLat: number,
  myLng: number,
  onUpdate: (users: NearbyUser[]) => void,
  maxCount: number = 50,
): Unsubscribe {
  const q = query(collection(db, USERS_COLLECTION));

  return onSnapshot(q, (snap) => {
    const users: NearbyUser[] = [];

    for (const docSnap of snap.docs) {
      const data = docSnap.data();
      const uid = docSnap.id;
      if (uid === myUid) continue;
      if (typeof data.lat !== 'number' || typeof data.lng !== 'number') continue;

      users.push(buildNearbyUser(uid, data, myLat, myLng));
    }

    // Sort by most recent activity first
    users.sort((a, b) => {
      const aMs = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
      const bMs = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
      return bMs - aMs;
    });

    onUpdate(users.slice(0, maxCount));
  }, (err) => {
    console.warn('[Location] Recent users listener error:', err.code ?? err.message);
  });
}

function buildNearbyUser(uid: string, data: any, myLat: number, myLng: number): NearbyUser {
  return {
    uid,
    lat: data.lat,
    lng: data.lng,
    geohash: data.geohash ?? '',
    nickname: data.nickname ?? uid.slice(0, 8),
    gender: data.gender ?? 'unknown',
    profileImageUrl: data.profileImageUrl ?? undefined,
    isOnline: data.isOnline ?? true,
    isBot: data.isBot ?? false,
    introduction: data.introduction ?? data.selfIntroduction ?? undefined,
    updatedAt: data.updatedAt,
    distance: calcDistance(myLat, myLng, data.lat, data.lng),
  };
}
